import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import AppLayout from "@/components/AppLayout";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import DevotionalList from "@/pages/DevotionalList";
import Devotional from "@/pages/Devotional";
import DailyReading from "@/pages/DailyReading";
import Scripture from "@/pages/Scripture";
import BibleBook from "@/pages/BibleBook";
import BibleReader from "@/pages/BibleReader";
import ReadingPlans from "@/pages/ReadingPlans";
import PlanDetail from "@/pages/PlanDetail";
import Guide from "@/pages/Guide";
import GuidedPractice from "@/pages/GuidedPractice";
import Journal from "@/pages/Journal";
import Profile from "@/pages/Profile";

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/auth" replace state={{ from: location }} />;
  return <AppLayout>{children}</AppLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function ThemeSync() {
  const { user } = useAuth();
  const { setTheme, setBackdrop } = useTheme();
  const applied = useRef(false);
  useEffect(() => {
    if (!user || applied.current) return;
    applied.current = true;
    if (user.theme) setTheme(user.theme);
    if (user.background) setBackdrop(user.background);
  }, [user, setTheme, setBackdrop]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/devotional" element={<Protected><DailyReading /></Protected>} />
      <Route path="/scripture" element={<Protected><Scripture /></Protected>} />
      <Route path="/scripture/:slug" element={<Protected><BibleBook /></Protected>} />
      <Route path="/scripture/:slug/read" element={<Protected><BibleReader /></Protected>} />
      <Route path="/plans" element={<Protected><ReadingPlans /></Protected>} />
      <Route path="/plans/:planId" element={<Protected><PlanDetail /></Protected>} />
      <Route path="/practices" element={<Protected><DevotionalList /></Protected>} />
      <Route path="/practices/guided/:slug" element={<Protected><GuidedPractice /></Protected>} />
      <Route path="/practice/:id" element={<Protected><Devotional /></Protected>} />
      <Route path="/guide" element={<Protected><Guide /></Protected>} />
      <Route path="/journal" element={<Protected><Journal /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const TOASTER_OPTIONS = { style: { fontFamily: "Outfit, sans-serif" } };

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ThemeSync />
            <AppRoutes />
            <Toaster position="top-center" toastOptions={TOASTER_OPTIONS} />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
